import { Link } from "@tanstack/react-router";
import { Phone, CheckCircle2, ArrowRight, Star } from "lucide-react";
import Navbar from "@/components/Navbar";
import CityHeroShared from "@/components/CityHeroShared";
import GeoAnswerBlock from "@/components/GeoAnswerBlock";
import { SERVICE_PAGES, type ServicePage } from "@/data/servicePages";
import { cityRoutePath } from "@/lib/cityRoutes";
import { revisionFor } from "@/data/contentRevisions";

const PHONE_DISPLAY = "(971) 328-2665";
const PHONE_HREF = "tel:9713282665";
const BASE = "https://sprinter-van-rentals.com";

/** Top metros linked from every service page — internal link equity to city pages. */
const TOP_CITIES: { slug: string; label: string }[] = [
  { slug: "losangeles", label: "Los Angeles" },
  { slug: "newyork", label: "New York" },
  { slug: "chicago", label: "Chicago" },
  { slug: "houston", label: "Houston" },
  { slug: "phoenix", label: "Phoenix" },
  { slug: "dallas", label: "Dallas" },
  { slug: "atlanta", label: "Atlanta" },
  { slug: "miami", label: "Miami" },
  { slug: "denver", label: "Denver" },
  { slug: "seattle", label: "Seattle" },
  { slug: "vegas", label: "Las Vegas" },
  { slug: "orlando", label: "Orlando" },
];

export function serviceJsonLd(page: ServicePage) {
  const url = `${BASE}/${page.slug}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${url}#service`,
      name: page.h1,
      alternateName: page.keyword,
      description: page.metaDescription,
      serviceType: page.keyword,
      url,
      provider: {
        "@type": "Organization",
        name: "Sprinter Van Rentals",
        url: BASE,
        telephone: "+1-971-328-2665",
      },
      areaServed: { "@type": "Country", name: "United States" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "12 & 15 Passenger Sprinter Van Rentals",
          item: BASE,
        },
        { "@type": "ListItem", position: 2, name: page.h1, item: url },
      ],
    },
    // Informational pages are articles as well as service offers.
    ...(page.slug === "maybach-sprinter-van"
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${url}#article`,
            headline: page.h1,
            alternativeHeadline: page.title,
            description: page.metaDescription,
            about: page.keyword,
            mainEntityOfPage: url,
            image: `${BASE}/sprinter913-91.png`,
            author: { "@type": "Organization", name: "Sprinter Van Rentals", url: BASE },
            publisher: {
              "@type": "Organization",
              name: "Sprinter Van Rentals",
              url: BASE,
              logo: { "@type": "ImageObject", url: `${BASE}/logo.png` },
            },
            ...(revisionFor(`/${page.slug}`) ? { dateModified: revisionFor(`/${page.slug}`) } : {}),
          },
        ]
      : []),
  ];
}

export default function ServiceLandingPage({ slug }: { slug: string }) {
  const page = SERVICE_PAGES[slug];
  if (!page) return null;

  const updated = revisionFor(`/${page.slug}`);
  const isReviewedGuide = ["sprinter-van-seating-capacity", "sprinter-van-images", "sprinter-van-limo-rental"].includes(slug);
  const hub = page.hubSlug ? SERVICE_PAGES[page.hubSlug] : undefined;

  return (
    <div className="min-h-screen bg-white">
      {/* Structured data lives here rather than in the route's head() so the
          full page dataset stays out of the shared entry bundle. It is still
          server-rendered into the HTML, which is what crawlers read. */}
      {serviceJsonLd(page).map((o, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(o) }}
        />
      ))}
      <Navbar />


      <CityHeroShared
        badge={page.keyword}
        headline={page.h1.toUpperCase()}
        intro={page.intro}
      />

      <main>
        {/* Breadcrumb — visible internal link back to the home page */}
        <nav aria-label="Breadcrumb" className="max-w-5xl mx-auto px-4 pt-8">
          <ol className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <li>
              <Link to="/" className="hover:text-[#F97316] underline underline-offset-2">
                12 &amp; 15 Passenger Sprinter Van Rentals
              </Link>
            </li>
            <li aria-hidden>/</li>
            <li className="text-slate-900 font-semibold">{page.h1}</li>
          </ol>
        </nav>

        {/* Answer-first summary — the chunk AI assistants lift */}
        <section className="max-w-5xl mx-auto px-4 pt-6 pb-10">
          <p className="text-lg sm:text-xl leading-relaxed text-slate-800 font-medium border-l-4 pl-5" style={{ borderColor: "#F97316" }}>
            {page.summary}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="/book?step=1"
              className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-display font-bold uppercase tracking-wide text-white"
              style={{ backgroundColor: "#F97316" }}
            >
              Book Online <ArrowRight size={18} />
            </a>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-display font-bold uppercase tracking-wide border-2 border-slate-900 text-slate-900"
            >
              <Phone size={18} /> {PHONE_DISPLAY}
            </a>
          </div>
        </section>

        {/* Topic-cluster hub link */}
        {hub && (
          <section className="max-w-5xl mx-auto px-4 pb-10">
            <a
              href={`/${page.hubSlug}`}
              className="block rounded-2xl border-2 border-slate-200 p-6 hover:border-[#F97316]"
            >
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#F97316" }}>
                Read the full guide
              </span>
              <span className="mt-1 block font-display text-xl sm:text-2xl font-black text-slate-900">
                {hub.h1}
              </span>
              <span className="mt-1 block text-slate-600">
                {hub.intro}
              </span>
            </a>
          </section>
        )}



        {/* Quick facts */}
        <section className="bg-slate-50 border-y border-slate-200">
          <div className="max-w-5xl mx-auto px-4 py-10">
            <h2 className="font-display text-2xl sm:text-3xl font-black text-slate-900 uppercase mb-6">
              {page.h1} at a glance
            </h2>
            <dl className="grid sm:grid-cols-2 gap-4">
              {page.facts.map((f) => (
                <div key={f.label} className="flex gap-3 bg-white rounded-lg border border-slate-200 p-4">
                  <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "#F97316" }} />
                  <div>
                    <dt className="font-bold text-slate-900">{f.label}</dt>
                    <dd className="text-slate-700">{f.value}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* Long-form body — one takeaway per H2 */}
        <article className="max-w-3xl mx-auto px-4 py-12 space-y-10">
          {page.sections.map((s) => (
            <section key={s.heading}>
              <h2 className="font-display text-2xl sm:text-3xl font-black text-slate-900 mb-3">{s.heading}</h2>
              <p className="text-[17px] leading-relaxed text-slate-700">{s.body}</p>
            </section>
          ))}

          <section>
            <h2 className="font-display text-2xl sm:text-3xl font-black text-slate-900 mb-3">
              Reserve with Sprinter Van Rentals
            </h2>
            <p className="text-[17px] leading-relaxed text-slate-700">
              Sprinter Van Rentals is a nationwide{" "}
              <Link to="/" className="font-semibold underline underline-offset-2" style={{ color: "#F97316" }}>
                12 and 15 passenger Sprinter van rental
              </Link>{" "}
               {isReviewedGuide
                 ? "company. Review the current vehicle options, quote and reservation terms for your city and dates."
                 : "company operating in more than 260 U.S. cities and 56 airports. Every reservation is all-in priced, every vehicle is late-model and professionally detailed, and 24/7 roadside assistance is standard."}
              Compare the entire fleet and check live availability on our{" "}
              <Link to="/" className="font-semibold underline underline-offset-2" style={{ color: "#F97316" }}>
                Sprinter van rental home page
              </Link>
              , or call {PHONE_DISPLAY} and we will size the right vehicle for your group.
            </p>
            {updated && <p className="mt-3 text-sm text-slate-500 flex items-center gap-2">
              <Star size={14} style={{ color: "#F97316" }} /> Last updated {updated}
            </p>}
          </section>
        </article>

        {/* GEO / AEO answer block with page-specific FAQ schema */}
        <GeoAnswerBlock
          heading={`${page.h1}: questions, answered`}
          summary={page.summary}
          facts={page.facts}
          faqs={page.faqs}
        />

        {/* Internal linking: cities + sibling services + home */}
        <section className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="font-display text-2xl sm:text-3xl font-black text-slate-900 uppercase mb-4">
            {page.h1} by city
          </h2>
          <p className="text-slate-700 mb-5">
            {isReviewedGuide ? "Check vehicle options and reservation terms in your city:" : "We deliver in more than 260 U.S. cities. A few of the busiest:"}
          </p>
          <div className="flex flex-wrap gap-2">
            {TOP_CITIES.map((c) => (
              <Link
                key={c.slug}
                to={cityRoutePath(c.slug)}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 hover:border-[#F97316] hover:text-[#F97316]"
              >
                {c.label} Sprinter Van Rentals
              </Link>
            ))}
          </div>

          <h2 className="font-display text-2xl sm:text-3xl font-black text-slate-900 uppercase mt-10 mb-4">
            Related services
          </h2>
          <ul className="grid sm:grid-cols-3 gap-4">
            {page.related.map((r) => {
              const rel = SERVICE_PAGES[r];
              if (!rel) return null;
              return (
                <li key={r}>
                  <a
                    href={`/${rel.slug}`}
                    className="block h-full rounded-xl border border-slate-200 p-5 hover:border-[#F97316]"
                  >
                    <span className="font-display font-black text-slate-900 block">{rel.h1}</span>
                    <span className="text-sm text-slate-600 line-clamp-3">{rel.intro}</span>
                  </a>
                </li>
              );
            })}
          </ul>

          <div className="mt-10 rounded-2xl bg-slate-900 text-white p-8 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-black uppercase">
              Ready to book your {page.keyword}?
            </h2>
            <p className="mt-2 text-slate-300">
               {isReviewedGuide ? "Check current vehicle options and pricing for your city and dates." : "Live availability and all-in pricing in every city we serve."}
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-display font-bold uppercase text-white"
                style={{ backgroundColor: "#F97316" }}
              >
                Check availability <ArrowRight size={18} />
              </Link>
              <a
                href={PHONE_HREF}
                className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-display font-bold uppercase border-2 border-white"
              >
                <Phone size={18} /> {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
